import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { LauncherPage } from '@/pages/Home';
import { LoginPage } from '@/pages/Login';
import { CallbackPage } from '@/pages/Callback';
import { ProfilePage } from '@/pages/Profile';
import { OrganizationsPage } from '@/pages/Admin/OrganizationsPage';
import { OrganizationDetailPage } from '@/pages/Admin/OrganizationDetailPage';
import { ConsoleLayout } from '@/pages/Console/ConsoleLayout';
import { DirectoryPage } from '@/pages/Console/DirectoryPage';
import { HierarchyPage } from '@/pages/Console/HierarchyPage';
import { ScopedAccountsPage } from '@/pages/Console/ScopedAccountsPage';
import { AppsPage } from '@/pages/Console/AppsPage';
import { AppsCreatePage } from '@/pages/Console/Apps/AppsCreatePage';
import { AppDetailPage } from '@/pages/Console/AppDetailPage';
import { AppEditPage } from '@/pages/Console/AppDetail/AppEditPage';
import { RoleMapPage } from '@/pages/Console/RoleMapPage';
import { AccountsPage } from '@/pages/Console/AccountsPage';
import { AccountProfilePage } from '@/pages/Console/Accounts/Profile/AccountProfilePage';
import { AuditPage } from '@/pages/Console/AuditPage';
import { ConfigPage } from '@/pages/Console/ConfigPage';
import { StoragePage } from '@/pages/Console/Storage/StoragePage';
import { PermissionsPage } from '@/pages/Console/PermissionsPage';
import { AuthProvider } from '@/auth/AuthProvider';

export function App() {
    return (
        <div className="portal-shell">
            <BrowserRouter>
                {/* EAA-1756: AuthProvider needs router context (useLocation) to know
                    the current path when deciding whether to trigger login(), so it
                    wraps Routes rather than wrapping BrowserRouter. */}
                <AuthProvider>
                    <Routes>
                        {/* EAA-1765: single visible login entry — reads ?returnTo and
                            drives Keycloak; the browser returns to the deep link. */}
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/callback" element={<CallbackPage />} />
                        <Route path="/profile" element={<ProfilePage />} />
                        {/* EAA-1776 (B5 slice 1): /console — every page inside the
                            shell gates itself on the server-computed capabilities[]
                            from /me/admin (§1b); portal-api re-enforces every call.
                            The existing EAA-1764 /organizations routes keep their
                            URLs (bookmarks) but now mount INSIDE the console shell.
                            EAA-1814 (IA change, canonical frame 09 "Inset shell
                            home"): `/` moves in here too — "Bảng điều khiển" is the
                            shell's first sidebar item now, not a launcher-then-console
                            split. `LauncherPage` adapts its own content by capability
                            (see its file docstring); the shell itself (nav visible or
                            not, per role) is ConsoleLayout's concern. */}
                        <Route element={<ConsoleLayout />}>
                            <Route path="/" element={<LauncherPage />} />
                            <Route path="/console" element={<DirectoryPage />} />
                            <Route path="/console/hierarchy" element={<HierarchyPage />} />
                            {/* EAA-1839: the org-scoped account browser + bulk
                                Level-assign (frames 34/35). Nested under
                                /console/hierarchy because the scope, the entry
                                point ("Xem tài khoản" on a tree node) and the
                                `console.orgs.view` gate are all that screen's —
                                NOT a second /console/accounts, which is the
                                unrelated realm-wide super-admin list. The đơn vị
                                rides in `?org=<uuid>` so the scope is a real,
                                shareable deep link. */}
                            <Route
                                path="/console/hierarchy/accounts"
                                element={<ScopedAccountsPage />}
                            />
                            <Route path="/console/apps" element={<AppsPage />} />
                            {/* EAA-1846: "Thêm ứng dụng" moved from a modal to a routed
                                page — MUST register before /console/apps/:code, same
                                reasoning as /console/accounts/:sub before /console/accounts
                                below: an unordered :code route would swallow /new as if
                                code === "new". */}
                            <Route path="/console/apps/new" element={<AppsCreatePage />} />
                            {/* EAA-1846 (frame 51 "App edit"): MUST register before
                                /console/apps/:code, same :code-vs-static-segment
                                ordering reasoning as /new above. */}
                            <Route path="/console/apps/:code/edit" element={<AppEditPage />} />
                            {/* EAA-1817 (frames 47-51): the app-detail page the
                                SRS frame table marked "Not shipped" until now. */}
                            <Route path="/console/apps/:code" element={<AppDetailPage />} />
                            {/* EAA-1841: the coverage board is no longer its own
                                screen — it is `/console/role-map`'s "Bảng phủ"
                                view (see RoleMapPage's docstring for why the two
                                routes were one screen all along). Redirect rather
                                than delete: the old path is in nav history, and
                                `?view=board` lands on exactly what it used to
                                show. */}
                            <Route
                                path="/console/role-coverage"
                                element={<Navigate to="/console/role-map?view=board" replace />}
                            />
                            <Route path="/console/role-map" element={<RoleMapPage />} />
                            {/* EAA-1820 (frame 72): profile page MUST register before the
                                list route — both share the `/console/accounts` prefix. */}
                            <Route path="/console/accounts/:sub" element={<AccountProfilePage />} />
                            <Route path="/console/accounts" element={<AccountsPage />} />
                            <Route path="/console/audit" element={<AuditPage />} />
                            {/* EAA-1842: read-only permission matrix. */}
                            <Route
                                path="/console/permissions"
                                element={<PermissionsPage />}
                            />
                            <Route path="/console/config" element={<ConfigPage />} />
                            {/* ECR-85: gated inside the page on console.storage.view. */}
                            <Route path="/console/storage" element={<StoragePage />} />
                            <Route path="/organizations" element={<OrganizationsPage />} />
                            <Route
                                path="/organizations/:orgId"
                                element={<OrganizationDetailPage />}
                            />
                        </Route>
                    </Routes>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}
